import type {
  ConnectInviteRequest,
  ConnectMember,
  ConnectMemberRole,
  ConnectSpaceDetail,
  OrgMember,
} from "@ando/core";
import {
  Badge,
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  RadioGroupHeadlessItem,
  RadioGroupHeadlessRoot,
  cn,
  toast,
} from "@ando/ui";
import { msg } from "@lingui/core/macro";
import { Trans, useLingui } from "@lingui/react/macro";
import { Building2, Check, Loader2, Mail, Search, Trash2, UserPlus } from "lucide-react";
import * as React from "react";

import { useOrgMembers } from "@/features/settings/hooks/useOrgMembers.js";
import { getEnv } from "@ando/core";
import { useInviteConnectMember, useRevokeConnectMember } from "../hooks/useConnectMutations.js";
import { ChatbotPanel } from "./ChatbotPanel.js";
import {
  ConfirmDialog,
  CopyableField,
  RailSectionHeader,
  formatRelative,
  roleLabel,
} from "./connectShared.js";

/**
 * MemberManager — owner-side guest invites + access list (Membri tab).
 *
 * Drops into the ``connect.$spaceId`` route's "Membri & accesso" extension
 * point. Lists every member as an avatar/initial + label + role + status badge
 * row, with a spatially-separated, danger-toned revoke (confirm + UNDO toast).
 * The invite flow mirrors ``ShareLinkDialog``: email + role → a magic link the
 * owner copies (the BE returns ``invite_token``; we build the public accept
 * URL the same way the public invite route resolves it).
 *
 * When ``space.mode === "chatbot"`` (or the owner switches to it), the
 * ``<ChatbotPanel>`` renders the public link + embed snippet + query caps in
 * place of the per-guest invite flow.
 *
 * DRY: reuses the pre-wired ``useInviteConnectMember`` / ``useRevokeConnectMember``
 * mutations + the shared ``ConfirmDialog`` + the ``getEnv().apiUrl`` origin the
 * rest of ``@ando/core`` uses to build public URLs.
 */

export interface MemberManagerProps {
  readonly space: ConnectSpaceDetail;
  readonly members: readonly ConnectMember[];
}

export function MemberManager({ space, members }: MemberManagerProps) {
  const { i18n } = useLingui();
  const locale = i18n.locale;
  const [inviteOpen, setInviteOpen] = React.useState(false);
  const [pendingRevoke, setPendingRevoke] = React.useState<ConnectMember | null>(null);

  const invite = useInviteConnectMember();
  const revoke = useRevokeConnectMember();
  const isChatbot = space.mode === "chatbot";

  const handleRevoke = React.useCallback(
    (member: ConnectMember) => {
      revoke.mutate(
        { spaceId: space.id, memberId: member.id },
        {
          onSuccess: () => {
            setPendingRevoke(null);
            toast.success(i18n._(msg`Accesso revocato`), {
              action: {
                label: i18n._(msg`Annulla`),
                onClick: () => {
                  if (!member.email) return;
                  invite.mutate(
                    {
                      spaceId: space.id,
                      invite: {
                        principal_type: "guest_email",
                        email: member.email,
                        role: (member.role as ConnectMemberRole) ?? "viewer",
                      },
                    },
                    {
                      onError: () => toast.error(i18n._(msg`Impossibile ripristinare l'invito`)),
                    },
                  );
                },
              },
            });
          },
          onError: () => toast.error(i18n._(msg`Impossibile revocare l'accesso`)),
        },
      );
    },
    [revoke, invite, space.id, i18n],
  );

  return (
    <div className="flex flex-col gap-2 py-2">
      {/* Chatbot spaces surface the public-link panel instead of per-guest
          invites; collaboration spaces show the invite flow. */}
      {isChatbot ? (
        <ChatbotPanel space={space} />
      ) : (
        <>
          <RailSectionHeader title={<Trans>Ospiti</Trans>} />

          {members.length > 0 ? (
            <ul className="flex flex-col gap-2">
              {members.map((m) => (
                <MemberRow
                  key={m.id}
                  member={m}
                  locale={locale}
                  onRevoke={() => setPendingRevoke(m)}
                />
              ))}
            </ul>
          ) : null}

          <Button
            variant="secondary"
            size="md"
            className="mt-1 w-full justify-center gap-1.5"
            onClick={() => setInviteOpen(true)}
          >
            <UserPlus size={14} strokeWidth={2.2} aria-hidden />
            <Trans>Invita ospite</Trans>
          </Button>

          <InviteDialog
            open={inviteOpen}
            onOpenChange={setInviteOpen}
            spaceId={space.id}
            invite={invite}
            members={members}
          />

          <ConfirmDialog
            open={pendingRevoke !== null}
            onOpenChange={(o) => {
              if (!o) setPendingRevoke(null);
            }}
            title={<Trans>Revocare l'accesso?</Trans>}
            description={
              <Trans>
                Questo ospite non potrà più aprire lo spazio né interrogarne le fonti. Puoi
                annullare subito dopo.
              </Trans>
            }
            confirmLabel={<Trans>Revoca</Trans>}
            cancelLabel={<Trans>Annulla</Trans>}
            pending={revoke.isPending}
            onConfirm={() => {
              if (pendingRevoke) handleRevoke(pendingRevoke);
            }}
          />
        </>
      )}
    </div>
  );
}
MemberManager.displayName = "MemberManager";

// ---------------------------------------------------------------------------
// Member row
// ---------------------------------------------------------------------------

function MemberRow({
  member,
  locale,
  onRevoke,
}: {
  readonly member: ConnectMember;
  readonly locale: string;
  readonly onRevoke: () => void;
}) {
  const { i18n } = useLingui();
  const label = member.email ?? i18n._(msg`Ospite`);
  const initial = (member.email ?? "?").trim().charAt(0).toUpperCase() || "?";
  const status = String(member.status ?? "").toLowerCase();
  const isPending = status === "pending" || status === "invited";
  const expiry = formatRelative(member.expires_at, locale);

  return (
    <li className="flex items-center gap-2.5 rounded-lg border border-hairline/70 bg-white/55 px-3 py-2.5">
      <span
        aria-hidden
        className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-cream-deep font-semibold text-[12px] text-ink-1"
      >
        {initial}
      </span>
      <div className="min-w-0 flex-1">
        <p className="truncate font-medium text-[12.5px] text-ink-1">{label}</p>
        <div className="mt-0.5 flex flex-wrap items-center gap-1.5">
          <Badge variant="secondary" className="py-0 text-[10px]">
            {roleLabel(member.role, i18n)}
          </Badge>
          {isPending ? (
            <Badge variant="warning" className="py-0 text-[10px]">
              <Trans>In attesa</Trans>
            </Badge>
          ) : (
            <Badge variant="success" className="py-0 text-[10px]">
              <Trans>Attivo</Trans>
            </Badge>
          )}
          {expiry ? (
            <span className="text-[10.5px] text-ink-2">
              <Trans>Invito scade</Trans> {expiry}
            </span>
          ) : null}
        </div>
      </div>
      <button
        type="button"
        onClick={onRevoke}
        aria-label={i18n._(msg`Revoca accesso`)}
        className={cn(
          "shrink-0 rounded-md p-1.5 text-ink-3 transition-colors",
          "hover:bg-rose-50 hover:text-rose-600",
          "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-rose-400/45",
        )}
      >
        <Trash2 size={14} strokeWidth={2} aria-hidden />
      </button>
    </li>
  );
}

// ---------------------------------------------------------------------------
// Invite dialog — two principals:
//   - "Collega" (internal): pick a colleague from MY org → invite by user_id
//     (``principal_type:'internal'``). Resolves immediately, no magic link.
//   - "Esterno" (external): email + role → magic link + copy (the original
//     ShareLinkDialog-style flow). ``org_user`` cross-org also stays here on
//     the email rail until a BE org-directory search exists for it.
// ---------------------------------------------------------------------------

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

type InviteMode = "colleague" | "external";

function InviteDialog({
  open,
  onOpenChange,
  spaceId,
  invite,
  members,
}: {
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
  readonly spaceId: string;
  readonly invite: ReturnType<typeof useInviteConnectMember>;
  /** Space members already invited — used to dim already-added colleagues. */
  readonly members: readonly ConnectMember[];
}) {
  const { i18n } = useLingui();
  const [mode, setMode] = React.useState<InviteMode>("colleague");
  const [email, setEmail] = React.useState("");
  const [touched, setTouched] = React.useState(false);
  const [role, setRole] = React.useState<ConnectMemberRole>("viewer");
  const [colleague, setColleague] = React.useState<OrgMember | null>(null);
  const [created, setCreated] = React.useState<ConnectMember | null>(null);

  React.useEffect(() => {
    if (open) {
      setMode("colleague");
      setEmail("");
      setTouched(false);
      setRole("viewer");
      setColleague(null);
      setCreated(null);
    }
  }, [open]);

  const emailValid = EMAIL_RE.test(email.trim());
  const inviteUrl = created?.invite_token ? buildInviteUrl(created.invite_token) : null;

  // ``user_id``s already on this Space (active OR pending) — so the picker can
  // mark them as already invited and block a duplicate.
  const alreadyInvitedUserIds = React.useMemo(
    () => new Set(members.map((m) => m.user_id).filter((id): id is string => Boolean(id))),
    [members],
  );

  const handleInviteExternal = () => {
    if (!emailValid) {
      setTouched(true);
      return;
    }
    const body: ConnectInviteRequest = {
      principal_type: "guest_email",
      email: email.trim(),
      role,
    };
    invite.mutate(
      { spaceId, invite: body },
      {
        onSuccess: (member) => {
          setCreated(member);
          toast.success(i18n._(msg`Invito creato`));
        },
        onError: () => toast.error(i18n._(msg`Impossibile creare l'invito`)),
      },
    );
  };

  const handleInviteColleague = () => {
    if (!colleague?.user_id) return;
    const body: ConnectInviteRequest = {
      principal_type: "internal",
      user_id: colleague.user_id,
      role,
    };
    invite.mutate(
      { spaceId, invite: body },
      {
        onSuccess: () => {
          const label = colleague.name?.trim() || colleague.email || i18n._(msg`Collega`);
          toast.success(i18n._(msg`${label} ora ha accesso a questo spazio`));
          onOpenChange(false);
        },
        onError: () => toast.error(i18n._(msg`Impossibile invitare il collega`)),
      },
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[460px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-[15px]">
            <UserPlus size={15} strokeWidth={2} aria-hidden /> <Trans>Invita allo spazio</Trans>
          </DialogTitle>
          <DialogDescription className="text-[12.5px]">
            {mode === "colleague" ? (
              <Trans>
                Aggiungi un collega della tua organizzazione: accede subito, in sola lettura, senza
                link da condividere.
              </Trans>
            ) : (
              <Trans>
                L'ospite riceve un link magico per accedere a questo spazio in sola lettura. Nessun
                account Ando richiesto.
              </Trans>
            )}
          </DialogDescription>
        </DialogHeader>

        {/* Mode toggle — colleague (internal, by user_id) vs external email. */}
        {!created ? (
          <div
            className="flex gap-1 rounded-lg border border-hairline/70 bg-cream-deep/40 p-1"
            role="tablist"
            aria-label={i18n._(msg`Tipo di invito`)}
          >
            {[
              {
                id: "colleague" as const,
                label: i18n._(msg`Collega`),
                icon: Building2,
              },
              { id: "external" as const, label: i18n._(msg`Esterno`), icon: Mail },
            ].map((opt) => {
              const Icon = opt.icon;
              const active = mode === opt.id;
              return (
                <button
                  key={opt.id}
                  type="button"
                  role="tab"
                  aria-selected={active}
                  onClick={() => setMode(opt.id)}
                  className={cn(
                    "flex flex-1 items-center justify-center gap-1.5 rounded-md px-3 py-1.5 font-medium text-[12px] transition-all",
                    "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ando-orange/45",
                    active
                      ? "bg-white text-ink-1 shadow-[0_1px_2px_rgba(0,0,0,0.05)]"
                      : "text-ink-3 hover:text-ink-1",
                  )}
                >
                  <Icon size={13} strokeWidth={2} aria-hidden />
                  {opt.label}
                </button>
              );
            })}
          </div>
        ) : null}

        {mode === "colleague" && !created ? (
          <div className="flex flex-col gap-3 py-2">
            <ColleaguePicker
              selected={colleague}
              onSelect={setColleague}
              alreadyInvitedUserIds={alreadyInvitedUserIds}
            />
            <RoleSelector role={role} onChange={setRole} />
          </div>
        ) : created && inviteUrl ? (
          <div className="flex flex-col gap-2 py-2">
            <CopyableField
              icon={<Mail size={13} strokeWidth={2} aria-hidden />}
              label={created.email ?? <Trans>Link di invito</Trans>}
              value={inviteUrl}
              openHref={inviteUrl}
              fieldAriaLabel={i18n._(msg`Link di invito`)}
            />
            <p className="px-0.5 text-[11px] text-ink-2">
              <Trans>Condividi questo link con l'ospite.</Trans>
            </p>
          </div>
        ) : (
          <div className="flex flex-col gap-3 py-2">
            <div className="flex flex-col gap-1.5">
              <label
                htmlFor="connect-invite-email"
                className="flex items-center gap-1 font-medium text-[12px] text-ink-1"
              >
                <Trans>Email dell'ospite</Trans>
                <span className="text-rose-500" aria-hidden>
                  *
                </span>
              </label>
              <input
                id="connect-invite-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                onBlur={() => setTouched(true)}
                placeholder={i18n._(msg`nome@azienda.it`)}
                aria-invalid={touched && !emailValid}
                className={cn(
                  "rounded-md border bg-white px-2.5 py-1.5 text-[12px] text-ink-1 focus-visible:outline-none focus-visible:ring-2",
                  touched && !emailValid
                    ? "border-rose-300 focus-visible:ring-rose-400/45"
                    : "border-black/10 focus-visible:ring-ando-orange/40",
                )}
              />
              {touched && !emailValid ? (
                <p className="text-[11px] text-rose-600">
                  <Trans>Inserisci un indirizzo email valido.</Trans>
                </p>
              ) : null}
            </div>

            <RoleSelector role={role} onChange={setRole} />

            {/* Honest cross-org copy — the BE first-classes existing Ando
                users (immediate in-app access + notification) and falls back
                to a magic link otherwise. We don't detect this client-side. */}
            <p className="px-0.5 text-[11px] text-ink-2 leading-snug">
              <Trans>
                Se è già su Ando, ottiene l'accesso subito e riceve una notifica. Altrimenti gli
                arriva un link magico via email.
              </Trans>
            </p>
          </div>
        )}

        <DialogFooter>
          <Button variant="ghost" size="md" onClick={() => onOpenChange(false)}>
            {created ? <Trans>Chiudi</Trans> : <Trans>Annulla</Trans>}
          </Button>
          {created ? null : mode === "colleague" ? (
            <Button
              variant="primary"
              size="md"
              className="gap-1.5"
              onClick={handleInviteColleague}
              disabled={invite.isPending || !colleague?.user_id}
            >
              {invite.isPending ? (
                <Loader2 className="animate-spin" size={14} aria-hidden />
              ) : (
                <UserPlus size={14} aria-hidden />
              )}
              <Trans>Aggiungi collega</Trans>
            </Button>
          ) : (
            <Button
              variant="primary"
              size="md"
              className="gap-1.5"
              onClick={handleInviteExternal}
              disabled={invite.isPending || !emailValid}
            >
              {invite.isPending ? (
                <Loader2 className="animate-spin" size={14} aria-hidden />
              ) : (
                <UserPlus size={14} aria-hidden />
              )}
              <Trans>Crea invito</Trans>
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// RoleSelector — the viewer/editor radio pair, shared by BOTH invite modes.
// ---------------------------------------------------------------------------

function RoleSelector({
  role,
  onChange,
}: {
  readonly role: ConnectMemberRole;
  readonly onChange: (role: ConnectMemberRole) => void;
}) {
  const { i18n } = useLingui();
  return (
    <div className="flex flex-col gap-1.5">
      <span className="font-medium text-[12px] text-ink-1">
        <Trans>Ruolo</Trans>
      </span>
      <RadioGroupHeadlessRoot
        value={role}
        onValueChange={(v) => onChange(v as ConnectMemberRole)}
        aria-label={i18n._(msg`Ruolo`)}
        className="grid grid-cols-2 gap-2"
      >
        {[
          {
            id: "viewer" as const,
            label: roleLabel("viewer", i18n),
            body: i18n._(msg`Può interrogare le fonti condivise, in sola lettura.`),
          },
          {
            id: "editor" as const,
            label: roleLabel("editor", i18n),
            // HONEST: editors today have the SAME read-only access as viewers —
            // member-to-member collaboration isn't wired yet. Don't promise a
            // capability that doesn't exist.
            body: i18n._(
              msg`Stesso accesso in sola lettura. Riservato a future funzioni di collaborazione.`,
            ),
          },
        ].map((r) => {
          const active = role === r.id;
          return (
            <RadioGroupHeadlessItem
              key={r.id}
              value={r.id}
              className={cn(
                "block w-full rounded-lg border px-3 py-2 text-left transition-all",
                active
                  ? "border-ando-orange/50 bg-ando-orange-soft/40"
                  : "border-hairline/70 bg-white/55 hover:bg-white/80",
                "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ando-orange/45",
              )}
            >
              <span className="block font-medium text-[12px] text-ink-1">{r.label}</span>
              <span className="mt-0.5 block text-[10.5px] text-ink-2">{r.body}</span>
            </RadioGroupHeadlessItem>
          );
        })}
      </RadioGroupHeadlessRoot>
    </div>
  );
}

// ---------------------------------------------------------------------------
// ColleaguePicker — searchable list of MY org's members (``principal_type:
// 'internal'`` by ``user_id``). Reuses the existing ``useOrgMembers`` source
// (the same one powering the admin Org members tab) — no new data path.
// ---------------------------------------------------------------------------

function ColleaguePicker({
  selected,
  onSelect,
  alreadyInvitedUserIds,
}: {
  readonly selected: OrgMember | null;
  readonly onSelect: (member: OrgMember) => void;
  readonly alreadyInvitedUserIds: ReadonlySet<string>;
}) {
  const { i18n } = useLingui();
  const { data: members, isLoading, isError } = useOrgMembers();
  const [query, setQuery] = React.useState("");

  // Only real, active accounts can be added by user_id — pending ghost invites
  // (``user_id === null``) have nothing to invite-as.
  const candidates = React.useMemo(() => {
    const q = query.trim().toLowerCase();
    return (members ?? [])
      .filter((m): m is OrgMember & { user_id: string } => Boolean(m.user_id) && m.is_active)
      .filter((m) => {
        if (!q) return true;
        return (
          (m.name ?? "").toLowerCase().includes(q) || (m.email ?? "").toLowerCase().includes(q)
        );
      });
  }, [members, query]);

  return (
    <div className="flex flex-col gap-1.5">
      <span className="font-medium text-[12px] text-ink-1">
        <Trans>Collega da invitare</Trans>
      </span>

      {/* Search box — filters by name or email. */}
      <div className="relative">
        <Search
          size={13}
          strokeWidth={2}
          aria-hidden
          className="-translate-y-1/2 absolute top-1/2 left-2.5 text-ink-3"
        />
        <input
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder={i18n._(msg`Cerca per nome o email…`)}
          aria-label={i18n._(msg`Cerca collega`)}
          className={cn(
            "w-full rounded-md border border-black/10 bg-white py-1.5 pr-2.5 pl-8 text-[12px] text-ink-1",
            "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ando-orange/40",
          )}
        />
      </div>

      <div
        aria-label={i18n._(msg`Colleghi della tua organizzazione`)}
        className="max-h-44 overflow-y-auto rounded-lg border border-hairline/70 bg-white/55"
      >
        {isLoading ? (
          <p className="flex items-center gap-2 px-3 py-3 text-[12px] text-ink-2">
            <Loader2 className="animate-spin" size={13} aria-hidden />
            <Trans>Caricamento colleghi…</Trans>
          </p>
        ) : isError ? (
          <p className="px-3 py-3 text-[12px] text-ink-2">
            <Trans>Impossibile caricare i colleghi.</Trans>
          </p>
        ) : candidates.length === 0 ? (
          <p className="px-3 py-3 text-[12px] text-ink-2">
            {query.trim() ? (
              <Trans>Nessun collega trovato.</Trans>
            ) : (
              <Trans>Nessun collega nella tua organizzazione.</Trans>
            )}
          </p>
        ) : (
          <ul className="flex flex-col">
            {candidates.map((m) => {
              const label = m.name?.trim() || m.email || i18n._(msg`Collega`);
              const initial = (m.name ?? m.email ?? "?").trim().charAt(0).toUpperCase() || "?";
              const isActive = selected?.user_id === m.user_id;
              const already = alreadyInvitedUserIds.has(m.user_id);
              return (
                <li key={m.id}>
                  <button
                    type="button"
                    aria-pressed={isActive}
                    disabled={already}
                    onClick={() => onSelect(m)}
                    className={cn(
                      "flex w-full items-center gap-2.5 px-3 py-2 text-left transition-colors",
                      "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ando-orange/45 focus-visible:ring-inset",
                      already
                        ? "cursor-not-allowed opacity-55"
                        : isActive
                          ? "bg-ando-orange-soft/45"
                          : "hover:bg-white/85",
                    )}
                  >
                    <span
                      aria-hidden
                      className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-cream-deep font-semibold text-[11px] text-ink-1"
                    >
                      {initial}
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="block truncate font-medium text-[12px] text-ink-1">
                        {label}
                      </span>
                      {m.email && m.name ? (
                        <span className="block truncate text-[10.5px] text-ink-2">{m.email}</span>
                      ) : null}
                    </span>
                    {already ? (
                      <Badge variant="secondary" className="shrink-0 py-0 text-[10px]">
                        <Trans>Già invitato</Trans>
                      </Badge>
                    ) : isActive ? (
                      <Check
                        size={14}
                        strokeWidth={2.4}
                        aria-hidden
                        className="shrink-0 text-ando-orange"
                      />
                    ) : null}
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** Build the public accept URL for an invite token (mirror of the public
 *  invite route ``/public/connect/invite/{token}``). */
function buildInviteUrl(token: string): string {
  const origin =
    typeof window !== "undefined" && window.location?.origin
      ? window.location.origin
      : getEnv().apiUrl;
  return `${origin}/connect/invite/${encodeURIComponent(token)}`;
}
